import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, Menu, X, Phone, Lock, ChevronDown } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

const RESOURCE_LINKS = [
  { label: "Benefits", path: "/benefits" },
  { label: "FAQs", path: "/faqs" },
  { label: "Banner Bar Wins", path: "/frame-comparison" },
  { label: "Where to Buy T-Posts", path: "/tpost-guide" },
  { label: "Dealer Assets", path: "/dealer-assets" },
  { label: "News", path: "/news" },
  { label: "Quality Guarantee", path: "/quality-guarantee" },
  { label: "About", path: "/about" },
  { label: "Contact", path: "/contact" },
];

const BUY_MENU = [
  { label: "Shop Online", path: "/products" },
  { label: "Find a Dealer", path: "/dealer-locator" },
  { label: "Wholesale Program", path: "/dealer-portal" },
  { label: "Volume & Multi-Location Quotes", path: "/corporate" },
];

const LINKS = [
  { label: "Compare All Kits", path: "/compare", testId: "nav-link-compare" },
  { label: "Ways to Buy", testId: "nav-buy", children: BUY_MENU },
  { label: "Videos", path: "/installation", testId: "nav-link-installation" },
  { label: "Resources", testId: "nav-resources", children: RESOURCE_LINKS },
];

const MOBILE_MAIN = [
  { label: "Shop Online", path: "/products", testId: "nav-link-shop-online" },
  { label: "Compare All Kits", path: "/compare", testId: "nav-link-compare" },
  { label: "Videos", path: "/installation", testId: "nav-link-installation" },
];

const MOBILE_BUY = BUY_MENU.filter((l) => l.label !== "Shop Online");

function NavDrop({ label, testId, items, align = "left" }) {
  return (
    <div className="group relative" data-testid={testId}>
      <button data-testid={`${testId}-trigger`} className="flex items-center gap-1 font-mono text-xs uppercase tracking-[0.18em] text-slate-400 transition-colors duration-200 hover:text-slate-100 group-hover:text-hivis">
        {label} <ChevronDown className="h-3 w-3 transition-transform duration-200 group-hover:rotate-180" />
      </button>
      <div className={`invisible absolute ${align === "right" ? "right-0" : "left-0"} top-full z-50 w-64 pt-3 opacity-0 transition-[opacity,visibility] duration-200 group-hover:visible group-hover:opacity-100`}>
        <div className="border border-girder bg-steel shadow-2xl">
          {items.map((r) => (
            <Link key={r.path + r.label} to={r.path} data-testid={`${testId}-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="block border-b border-girder px-5 py-3 font-mono text-xs uppercase tracking-wider text-slate-300 transition-colors last:border-0 hover:bg-girder/50 hover:text-hivis">
              {r.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Navbar() {
  const { count, setOpen } = useCart();
  const { dealer } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 z-50 w-full border-b transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled ? "border-girder bg-obsidian/85 backdrop-blur-xl" : "border-transparent bg-gradient-to-b from-obsidian/90 via-obsidian/60 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between gap-5 px-4 sm:px-6 lg:px-12 xl:gap-7">
        <Link to="/" className="group flex shrink-0 items-center" data-testid="nav-logo">
          <img src="/assets/brand/logo-horizontal.png" alt="The Banner Bar" className="h-10 w-auto" />
        </Link>

        <nav className="hidden items-center gap-5 xl:gap-7 lg:flex" aria-label="Primary">
          {LINKS.map((l) =>
            l.children ? (
              <NavDrop key={l.label} label={l.label} testId={l.testId} items={l.children} align={l.label === "Resources" ? "right" : "left"} />
            ) : (
              <NavLink
                key={l.path}
                to={l.path}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `relative font-mono text-xs uppercase tracking-[0.18em] transition-colors duration-200 ${
                    isActive ? "text-hivis" : "text-slate-400 hover:text-slate-100"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-4">
          <a href="tel:9706505173" data-testid="nav-phone" className="hidden items-center gap-2 whitespace-nowrap font-mono text-xs tracking-wider text-slate-400 transition-colors hover:text-hivis xl:flex">
            <Phone className="h-3.5 w-3.5" /> 970-650-5173
          </a>
          {dealer ? (
            <Link to="/dealer-portal" data-testid="nav-dealer-badge" className="hidden items-center gap-1 border border-hivis/40 px-2 py-1 font-mono text-[10px] uppercase tracking-widest text-hivis transition-colors hover:bg-hivis/10 sm:flex">
              <Lock className="h-3 w-3" /> Dealer
            </Link>
          ) : (
            <Link to="/dealer-portal" data-testid="nav-dealer-login" className="hidden items-center gap-1.5 border border-girder px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-slate-300 transition-colors hover:border-hivis hover:text-hivis sm:flex">
              <Lock className="h-3 w-3" /> Dealer Login
            </Link>
          )}
          <button
            data-testid="cart-drawer-trigger"
            onClick={() => setOpen(true)}
            className="relative flex h-10 w-10 items-center justify-center border border-girder text-slate-300 transition-colors duration-200 hover:border-hivis hover:text-hivis"
            aria-label="Open cart"
          >
            <ShoppingCart className="h-4 w-4" />
            {count > 0 && (
              <span data-testid="cart-count" className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center bg-hivis font-mono text-[10px] font-bold text-obsidian">
                {count}
              </span>
            )}
          </button>
          <button
            data-testid="mobile-menu-trigger"
            onClick={() => setMobileOpen(true)}
            className="flex h-10 w-10 items-center justify-center border border-girder text-slate-300 lg:hidden"
            aria-label="Open menu"
          >
            <Menu className="h-4 w-4" />
          </button>
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 overflow-y-auto bg-obsidian/[0.98] backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex h-20 items-center justify-between px-4 sm:px-6">
              <img src="/assets/brand/logo-horizontal.png" alt="The Banner Bar" className="h-9 w-auto" />
              <button onClick={() => setMobileOpen(false)} data-testid="mobile-menu-close" aria-label="Close menu" className="flex h-10 w-10 items-center justify-center border border-girder text-slate-300">
                <X className="h-4 w-4" />
              </button>
            </div>
            <nav className="flex flex-col gap-1 px-6 pb-28 pt-8" aria-label="Mobile">
              {MOBILE_MAIN.map((l, i) => (
                <motion.button
                  key={l.path + l.label}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0, transition: { delay: 0.05 * i } }}
                  onClick={() => { setMobileOpen(false); navigate(l.path); }}
                  data-testid={`mobile-${l.testId}`}
                  className="border-b border-girder py-4 text-left font-display text-3xl font-bold uppercase tracking-tight text-slate-200 transition-colors hover:text-hivis"
                >
                  {l.label}
                </motion.button>
              ))}
              <div className="mt-6 border-t border-girder pt-4">
                <p className="pb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Ways to Buy</p>
                <div className="grid grid-cols-2 gap-1">
                  {MOBILE_BUY.map((r) => (
                    <button
                      key={r.path + r.label}
                      onClick={() => { setMobileOpen(false); navigate(r.path); }}
                      data-testid={`mobile-nav-buy-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="py-2 text-left font-mono text-xs uppercase tracking-wider text-slate-400 transition-colors hover:text-hivis"
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mt-6 border-t border-girder pt-4">
                <p className="pb-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Resources</p>
                <div className="grid grid-cols-2 gap-1">
                  {RESOURCE_LINKS.map((r) => (
                    <button
                      key={r.path}
                      onClick={() => { setMobileOpen(false); navigate(r.path); }}
                      data-testid={`mobile-nav-resource-${r.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="py-2 text-left font-mono text-xs uppercase tracking-wider text-slate-400 transition-colors hover:text-hivis"
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              </div>
            </nav>
          </motion.div>
        )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}
